// Offline meal-mutation outbox (DESIGN §2.3, §10 M4 exit, issue #142).
//
// Buffers POST /v1/meals submissions to IndexedDB while the device is
// offline so the user can keep logging meals without losing data, then
// drains the queue when the network comes back.
//
// Replay safety leans on the API's `Idempotency-Key` middleware
// (`apps/api/src/middleware/idempotency.ts`): each outbox entry uses its
// id as the key, so a meal that was submitted twice (online, then
// drained again because the success notification didn't reach the
// browser) returns the same cached body the second time.

import type { MealInputItem, MealSlot } from '@food-diary/shared';
import { type UseStore, createStore, del, get, keys, set } from 'idb-keyval';
import { ApiError, apiFetch } from './api';

const DB_NAME = 'food-diary-outbox';
const STORE_NAME = 'meals';

export type OutboxEntry = {
  /** Client-generated UUID — also used as the Idempotency-Key. */
  readonly id: string;
  /** Free-text path: present iff this entry was enqueued from the textarea. */
  readonly rawInput?: string;
  /** Quick-log path: present iff this entry was enqueued from a chip / autocomplete pick. */
  readonly items?: readonly MealInputItem[];
  /** Optional meal slot — passed through verbatim on replay. */
  readonly mealSlot?: MealSlot;
  /** Wall-clock ms when the entry was first enqueued. Drained in arrival order. */
  readonly enqueuedAt: number;
  /** Bumped each time the drain hits a transient failure on this entry. */
  readonly attempts: number;
};

export type OutboxStorage = {
  list(): Promise<readonly OutboxEntry[]>;
  put(entry: OutboxEntry): Promise<void>;
  remove(id: string): Promise<void>;
};

/** Default IDB-backed storage. Lazily constructed so SSR doesn't trip on `indexedDB`. */
function idbStorage(): OutboxStorage {
  // eslint-disable-next-line functional/no-let
  let store: UseStore | null = null;
  const handle = (): UseStore => {
    if (store) return store;
    store = createStore(DB_NAME, STORE_NAME);
    return store;
  };
  return {
    async list() {
      // `keys()` returns IDBValidKey[]. We only ever store string keys
      // (the entry id), so narrowing here is safe and keeps the
      // `@typescript-eslint/no-base-to-string` rule happy.
      const allKeys = await keys(handle());
      const ks = allKeys.filter((k): k is string => typeof k === 'string');
      const entries = await Promise.all(ks.map((k) => get<OutboxEntry>(k, handle())));
      return entries
        .filter((e): e is OutboxEntry => e !== undefined)
        .sort((a, b) => a.enqueuedAt - b.enqueuedAt);
    },
    async put(entry) {
      await set(entry.id, entry, handle());
    },
    async remove(id) {
      await del(id, handle());
    },
  };
}

// Module-level storage handle. Tests swap this via `__setStorage`.
// eslint-disable-next-line functional/no-let
let storage: OutboxStorage | null = null;

function getStorage(): OutboxStorage {
  if (storage) return storage;
  storage = idbStorage();
  return storage;
}

/** Test-only: replace the storage backend (e.g. with an in-memory Map). */
export function __setStorage(custom: OutboxStorage | null): void {
  storage = custom;
}

export async function enqueueMeal(
  payload:
    | string
    | { readonly rawInput: string; readonly mealSlot?: MealSlot }
    | { readonly items: readonly MealInputItem[]; readonly mealSlot?: MealSlot },
): Promise<OutboxEntry> {
  const id = crypto.randomUUID();
  const base = {
    id,
    enqueuedAt: Date.now(),
    attempts: 0,
  };
  // Legacy single-string call: keep the original signature working so we
  // don't have to update every call site at once.
  if (typeof payload === 'string') {
    const entry: OutboxEntry = { ...base, rawInput: payload };
    await getStorage().put(entry);
    return entry;
  }
  // `exactOptionalPropertyTypes` forbids assigning `undefined` to an
  // optional field — build the entry incrementally so we only set
  // `mealSlot` when the caller actually provided one.
  const entry: OutboxEntry =
    'items' in payload
      ? {
          ...base,
          items: payload.items,
          ...(payload.mealSlot ? { mealSlot: payload.mealSlot } : {}),
        }
      : {
          ...base,
          rawInput: payload.rawInput,
          ...(payload.mealSlot ? { mealSlot: payload.mealSlot } : {}),
        };
  await getStorage().put(entry);
  return entry;
}

export async function listOutboxEntries(): Promise<readonly OutboxEntry[]> {
  return getStorage().list();
}

export async function removeOutboxEntry(id: string): Promise<void> {
  await getStorage().remove(id);
}

async function bumpAttempts(entry: OutboxEntry): Promise<void> {
  await getStorage().put({ ...entry, attempts: entry.attempts + 1 });
}

export type DrainOutcome = {
  readonly succeeded: number;
  readonly droppedPermanent: number;
  readonly remaining: number;
};

/**
 * POST every queued meal in arrival order. On a 4xx the entry is dropped
 * — the server has rejected it permanently and replaying won't help. On
 * a 5xx or network error the attempt count is bumped and the entry stays
 * queued for the next drain.
 *
 * `fetcher` is injected for tests; production uses the shared `apiFetch`.
 */
export async function drainMealOutbox(fetcher: typeof apiFetch = apiFetch): Promise<DrainOutcome> {
  const entries = await listOutboxEntries();
  // eslint-disable-next-line functional/no-let
  let succeeded = 0;
  // eslint-disable-next-line functional/no-let
  let droppedPermanent = 0;
  for (const entry of entries) {
    try {
      // Send whichever payload this entry was enqueued with. The server
      // accepts both shapes on `POST /v1/meals` (see `MealInput` in
      // packages/shared); rawInput goes through the resolver pipeline,
      // items goes through the LLM-bypass quick-log path.
      const body: Record<string, unknown> = {};
      if (entry.items) body.items = entry.items;
      else if (entry.rawInput !== undefined) body.rawInput = entry.rawInput;
      if (entry.mealSlot) body.mealSlot = entry.mealSlot;
      await fetcher('/v1/meals', {
        method: 'POST',
        headers: { 'Idempotency-Key': entry.id },
        body: JSON.stringify(body),
      });
      await removeOutboxEntry(entry.id);
      succeeded += 1;
    } catch (error) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        await removeOutboxEntry(entry.id);
        droppedPermanent += 1;
      } else {
        await bumpAttempts(entry);
        // Stop draining on the first transient failure — preserves
        // arrival order, and the next online event will retry.
        break;
      }
    }
  }
  const remainingEntries = await listOutboxEntries();
  return { succeeded, droppedPermanent, remaining: remainingEntries.length };
}
